import { Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Dialog } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { CheckboxModule } from 'primeng/checkbox';
import { finalize, Subscription } from 'rxjs';
import { NotificationTemplatesService } from '../../../../services/notifications/notification-templates-service';
import { NotificationCampaignsService, AudienceType } from '../../../../services/notifications/notification-campaigns-service';
import { CustomerService } from '../../../../services/customers/customer-service';
import { CustomerGroupsService } from '../../../../services/customer-groups/customer-groups-service';
import { CustomerGroup } from '../../../../models/customer-group';
import { NotificationTemplate } from '../../../../models/notification-template';
import { enumToOptions } from '../../../utility/enumToOptions';
import { CustomerStatus } from '../../../../models/enums';

type Step = 'audience' | 'customers' | 'compose' | 'success';

@Component({
  selector: 'app-send-notification',
  imports: [FormsModule, Dialog, ButtonModule, SelectModule, MultiSelectModule, CheckboxModule],
  templateUrl: './send-notification.html',
  styleUrl: './send-notification.scss',
})
export class SendNotification implements OnInit {
  private groupsService = inject(CustomerGroupsService);
  private templatesService = inject(NotificationTemplatesService);
  private campaignsService = inject(NotificationCampaignsService);
  private customerService = inject(CustomerService);

  @Output() visibilityChange = new EventEmitter<boolean>();
  @Output() sent = new EventEmitter<void>();

  @Input()
  get visibility() { return this._visibility; }
  set visibility(v: boolean) {
    this._visibility = v;
    if (v) this.reset();
  }
  private _visibility = false;

  step = signal<Step>('audience');
  loading = signal(false);
  sending = signal(false);

  // Audience
  audienceType: AudienceType = 'CUSTOMER_GROUP';
  audienceTypes = [
    { label: 'Customer Group',  value: 'CUSTOMER_GROUP' as AudienceType,  icon: 'fa-solid fa-layer-group', desc: 'Send to all members of a customer group' },
    { label: 'By status',       value: 'CUSTOMER_STATUS' as AudienceType, icon: 'fa-solid fa-filter',      desc: 'Send to customers by account status' },
    { label: 'Customers',       value: 'CUSTOMERS' as AudienceType,       icon: 'fa-solid fa-users',       desc: 'Hand-pick specific customers' },
    { label: 'All customers',   value: 'ALL_CUSTOMERS' as AudienceType,   icon: 'fa-solid fa-globe',       desc: 'Send to every customer' },
  ];

  // CUSTOMER_GROUP
  groups: CustomerGroup[] = [];
  selectedGroupId: number | null = null;

  // CUSTOMERS multiselect
  customerOptions: { label: string; value: number; email: string; status: string; initials: string }[] = [];
  selectedCustomerValues: number[] = [];
  private custPage = 0;
  private custHasNext = true;
  private custLoading = false;
  private custSearch = '';
  private custPanelScrollListener: (() => void) | null = null;
  private custFilterDebounce: ReturnType<typeof setTimeout> | null = null;
  private custRequest: Subscription | null = null;

  // CUSTOMER_STATUS
  availableStatuses = enumToOptions(CustomerStatus);
  selectedStatuses: string[] = [];

  // Compose
  templates: NotificationTemplate[] = [];
  composeTab: 'template' | 'custom' = 'template';
  selectedTemplateId: number | null = null;
  customTitle = '';
  customBody = '';

  // Success
  successTopicName = '';
  successCount = 0;

  get selectedGroup(): CustomerGroup | undefined {
    return this.groups.find(g => g.id === this.selectedGroupId);
  }

  get selectedCustomerIds(): number[] { return this.selectedCustomerValues; }

  get selectedTemplate(): NotificationTemplate | undefined {
    return this.templates.find(t => t.id === this.selectedTemplateId);
  }

  get customBodyLength(): number { return this.customBody.length; }


  get selectedAudienceType() {
    return this.audienceTypes.find(a => a.value === this.audienceType);
  }

  get audienceSummary(): string {
    switch (this.audienceType) {
      case 'CUSTOMER_GROUP':  return this.selectedGroup ? `${this.selectedGroup.name} · ${this.selectedGroup.memberCount} members` : 'Select a group';
      case 'CUSTOMERS':       return `${this.selectedCustomerIds.length} customers selected`;
      case 'CUSTOMER_STATUS': return this.selectedStatuses.length ? `${this.selectedStatuses.length} status(es) selected` : 'Select statuses';
      case 'ALL_CUSTOMERS':   return 'All customers';
    }
  }

  get canProceed(): boolean {
    switch (this.audienceType) {
      case 'CUSTOMER_GROUP':  return !!this.selectedGroupId;
      case 'CUSTOMERS':       return this.selectedCustomerIds.length > 0;
      case 'CUSTOMER_STATUS': return this.selectedStatuses.length > 0;
      case 'ALL_CUSTOMERS':   return true;
    }
  }

  ngOnInit() {
    this.loadGroups();
    this.loadTemplates();
  }

  loadGroups() {
    this.groupsService.getGroups({ sortBy: 'createdAt', sortDirection: 'DESC', size: 100 }).subscribe(res => {
      this.groups = res.data?.content ?? [];
      if (this.groups.length && !this.selectedGroupId) {
        this.selectedGroupId = this.groups[0].id;
      }
    });
  }

  loadTemplates() {
    this.templatesService.getTemplates().subscribe(res => {
      const raw = res.data as NotificationTemplate[] | { content: NotificationTemplate[] };
      this.templates = Array.isArray(raw) ? raw : (raw?.content ?? []);
    });
  }

  onAudienceTypeChange(type: AudienceType) {
    this.audienceType = type;
    if (type === 'CUSTOMERS' && !this.customerOptions.length) this.loadCustPage();
  }

  toggleStatus(value: string) {
    const idx = this.selectedStatuses.indexOf(value);
    if (idx >= 0) this.selectedStatuses.splice(idx, 1);
    else this.selectedStatuses.push(value);
  }

  onCustPanelShow() {
    if (!this.customerOptions.length) this.loadCustPage();
    setTimeout(() => {
      const wrapper = document.querySelector('.p-multiselect-panel .p-multiselect-items-wrapper') as HTMLElement;
      if (!wrapper) return;
      this.custPanelScrollListener = () => {
        if (wrapper.scrollHeight - wrapper.scrollTop - wrapper.clientHeight < 150) this.loadCustPage();
      };
      wrapper.addEventListener('scroll', this.custPanelScrollListener);
    }, 80);
  }

  onCustPanelHide() {
    const wrapper = document.querySelector('.p-multiselect-panel .p-multiselect-items-wrapper') as HTMLElement;
    if (wrapper && this.custPanelScrollListener) wrapper.removeEventListener('scroll', this.custPanelScrollListener);
    this.custPanelScrollListener = null;
  }

  onCustFilter(event: { filter?: string }) {
    const search = event?.filter ?? '';
    if (search === this.custSearch) return;
    if (this.custFilterDebounce) clearTimeout(this.custFilterDebounce);
    this.custFilterDebounce = setTimeout(() => {
      this.custSearch = search;
      this.custPage = 0;
      this.custHasNext = true;
      // Cancel any in-flight request for the previous search
      this.custRequest?.unsubscribe();
      this.custLoading = false;
      // Keep already-selected customers so they stay checked, then load results for the new search
      const kept = new Set(this.selectedCustomerValues);
      this.customerOptions = this.customerOptions.filter(o => kept.has(o.value));
      this.loadCustPage();
    }, 1000);
  }

  private loadCustPage() {
    if (this.custLoading || !this.custHasNext) return;
    this.custLoading = true;
    this.custRequest = this.customerService.getCustomersList({
      page: this.custPage, size: 20, sortBy: 'updatedAt', sortDirection: 'DESC',
      search: this.custSearch || undefined,
    }).pipe(finalize(() => { this.custLoading = false; })).subscribe({
      next: (res) => {
        interface CustomerRaw { firstName: string; lastName: string; id: string | number; email?: string; status?: string }
        const items = res.data.content.map((c: CustomerRaw) => ({
          label: `${c.firstName} ${c.lastName}`,
          value: Number(c.id),
          email: c.email ?? '',
          status: c.status ?? '',
          initials: `${c.firstName?.[0] ?? ''}${c.lastName?.[0] ?? ''}`.toUpperCase(),
        }));
        const existing = new Set(this.customerOptions.map(o => o.value));
        this.customerOptions = [...this.customerOptions, ...items.filter(i => !existing.has(i.value))];
        this.custHasNext = items.length === 20;
        this.custPage++;
      },
    });
  }

  goToCompose() {
    this.step.set('compose');
  }

  send() {
    const audience: { type: AudienceType; groupId?: number; customerIds?: number[]; statuses?: string[] } = { type: this.audienceType };
    if (this.audienceType === 'CUSTOMER_GROUP' && this.selectedGroupId != null) audience.groupId = this.selectedGroupId;
    if (this.audienceType === 'CUSTOMERS')        audience.customerIds = this.selectedCustomerIds;
    if (this.audienceType === 'CUSTOMER_STATUS')  audience.statuses    = this.selectedStatuses;

    const payload: { audience: typeof audience; templateId?: number; title?: string; body?: string } = { audience };
    if (this.composeTab === 'template' && this.selectedTemplateId) {
      payload.templateId = this.selectedTemplateId;
    } else {
      payload.title = this.customTitle;
      payload.body  = this.customBody;
    }

    this.sending.set(true);
    this.campaignsService.sendCampaign(payload).pipe(
      finalize(() => this.sending.set(false))
    ).subscribe({
      next: () => {
        this.successTopicName = this.audienceSummary;
        this.step.set('success');
        this.sent.emit();
      },
    });
  }

  close() {
    this._visibility = false;
    this.visibilityChange.emit(false);
  }

  categoryLabel(cat: string): string {
    return cat ? cat.charAt(0) + cat.slice(1).toLowerCase() : '';
  }

  customerStatusStyle(status: string): { background: string; color: string } {
    const map: Record<string, { background: string; color: string }> = {
      'ACTIVE':                    { background: '#dcfce7', color: '#16a34a' },
      'BANNED':                    { background: '#fee2e2', color: '#dc2626' },
      'AWAITING_OTP_VERIFICATION': { background: '#fef9c3', color: '#854d0e' },
      'AWAITING_PAYMENT':          { background: '#fef9c3', color: '#854d0e' },
      'PAYMENT_PENDING':           { background: '#fef9c3', color: '#854d0e' },
      'PAYMENT_FAILED':            { background: '#fee2e2', color: '#dc2626' },
      'AWAITING_DOCUMENT_UPLOAD':  { background: '#ffedd5', color: '#c2410c' },
      'AWAITING_DOCUMENT_REVIEW':  { background: '#ffedd5', color: '#c2410c' },
      'DOCUMENTS_REJECTED':        { background: '#fee2e2', color: '#dc2626' },
    };
    return map[status] ?? { background: '#f1f5f9', color: '#64748b' };
  }

  private reset() {
    this.step.set('audience');
    this.audienceType = 'CUSTOMER_GROUP';
    this.selectedGroupId = null;
    this.custRequest?.unsubscribe();
    this.customerOptions = [];
    this.selectedCustomerValues = [];
    this.custPage = 0;
    this.custHasNext = true;
    this.custSearch = '';
    this.selectedStatuses = [];
    this.composeTab = 'template';
    this.selectedTemplateId = null;
    this.customTitle = '';
    this.customBody = '';
    this.loadGroups();
    this.loadTemplates();
  }
}
